import { ApiClient } from './apiClient';
import { MapsLinkResult } from './maps/types';
import { validateGoogleMapsUrl } from './maps/validation';
import { convertOpeningHoursFormat, detectBusinessFeatures, cleanWebsiteField } from './maps/transforms';

export class MapsService {
  static async processGoogleMapsLink(mapsUrl: string): Promise<MapsLinkResult> {
    // Validate URL before processing
    if (!validateGoogleMapsUrl(mapsUrl)) {
      throw new Error('Invalid Google Maps URL format');
    }
    
    try {
      // Add request timeout and better error handling
      const apiResult = await ApiClient.invokeFunction<MapsLinkResult>(
        'process-maps-link-v2',
        { mapsUrl: mapsUrl.trim() },
        { timeout: 75000 } // Increased timeout to 75 seconds for complex place lookups
      );
      
      // Validate the response structure
      if (!apiResult) {
        throw new Error('No data received from processing service');
      }
      
      if (!apiResult.name && !apiResult.address) {
        throw new Error('Incomplete place information received');
      }
      
      // Use the detailed opening hours from API response (already properly formatted)
      // Fallback to converting if detailedOpeningHours is not provided
      const detailedOpeningHours = apiResult.detailedOpeningHours || convertOpeningHoursFormat(apiResult.openingHours);
      const businessFeatures = detectBusinessFeatures(apiResult.type, apiResult.name);
      
      // Clean up website field - handle both string and object formats
      const cleanWebsite = cleanWebsiteField(apiResult.website);
      
      // Return with proper fallbacks and cleanup
      const result: MapsLinkResult = {
        name: apiResult.name || 'Unknown Place',
        type: apiResult.type || 'other',
        address: apiResult.address || '',
        coordinates: apiResult.coordinates && 
                    typeof apiResult.coordinates.lat === 'number' && 
                    typeof apiResult.coordinates.lng === 'number' && 
                    !isNaN(apiResult.coordinates.lat) && 
                    !isNaN(apiResult.coordinates.lng)
          ? apiResult.coordinates 
          : undefined,
        place_id: apiResult.place_id || undefined,
        website: cleanWebsite,
        phone: apiResult.phone || undefined,
        detailedOpeningHours: detailedOpeningHours,
        originalMapsUrl: apiResult.originalMapsUrl || mapsUrl.trim(),
        // Add business features
        hasFood: businessFeatures.hasFood,
        servesAlcohol: businessFeatures.servesAlcohol,
        petFriendly: businessFeatures.petFriendly
      };
      
      return result;
      
    } catch (error) {
      console.error('[MapsService] Processing error:', error);
      
      // Enhanced error classification and user-friendly messages
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
        throw new Error('Request timed out. The location processing is taking too long - please try again.');
      }
      
      if (errorMessage.includes('Invalid Google Maps URL') || errorMessage.includes('URL must be a valid')) {
        throw new Error('Invalid Google Maps link. Please use the "Share" button in Google Maps to get a proper link.');
      }
      
      if (errorMessage.includes('Could not find place') || errorMessage.includes('No places found')) {
        throw new Error('Could not find the place from this link. Make sure it points to a specific business or location.');
      }
      
      if (errorMessage.includes('Place not found')) {
        throw new Error('The place could not be found in Google Maps. Please check the link or try a different location.');
      }
      
      if (errorMessage.includes('API access denied') || errorMessage.includes('permissions')) {
        throw new Error('Unable to access place information at the moment. Please try again later or fill the form manually.');
      }
      
      if (errorMessage.includes('Google Places API configuration')) {
        throw new Error('Service temporarily unavailable. Please try again in a few moments.');
      }
      
      if (errorMessage.includes('Failed to process') || errorMessage.includes('non-2xx status')) {
        throw new Error('Unable to process this Google Maps link. Please try copying the link directly from the "Share" button.');
      }
      
      // Generic fallback for unexpected errors
      throw new Error('Failed to process the Google Maps link. Please check the link format or try filling the form manually.');
    }
  }

}
